import axiosInstance from './axios';

// Public Custom Cake option groups for one product (task-191). Mirrors
// backend/app/schemas/cc_options.py PublicOptionGroup / PublicOptionItem: only
// enabled groups/items are returned, `price` is the EFFECTIVE price (per-product
// override when set, else default_price). Display only -- the server recomputes
// every price at cart/checkout time from the selected ids.
export interface CCOptionItem {
  id: number;
  name: string;
  name_zh?: string | null;
  price: number;
  default_price: number;
  sort_order: number;
}

export interface CCOptionGroup {
  id: number;
  name: string;
  name_zh?: string | null;
  selection_mode: 'single' | 'multiple';
  required: boolean;
  sort_order: number;
  items: CCOptionItem[];
}

export const fetchCCOptions = async (productId: number): Promise<CCOptionGroup[]> => {
  const response = await axiosInstance.get(`/products/${productId}/options`);
  return response.data;
};

/** Anything other than exactly 'multiple' is treated as single-select (safer default). */
export const isCCSingleSelect = (group: Pick<CCOptionGroup, 'selection_mode'>): boolean =>
  group.selection_mode !== 'multiple';

/**
 * Initial selection for a set of groups: the first item of every REQUIRED group
 * (single or multiple), nothing for optional groups.
 */
export const defaultCCSelections = (groups: CCOptionGroup[]): Record<number, number[]> => {
  const out: Record<number, number[]> = {};
  groups.forEach(g => {
    if (g.required && g.items.length > 0) out[g.id] = [g.items[0].id];
  });
  return out;
};

/** Toggle an item in a group's selection, honouring single/multiple and required. */
export const toggleCCSelection = (
  selections: Record<number, number[]>,
  group: CCOptionGroup,
  itemId: number
): Record<number, number[]> => {
  const existing = selections[group.id] ?? [];
  const selected = existing.includes(itemId);
  if (isCCSingleSelect(group)) {
    // Radio: a required group can't be cleared; an optional one can be deselected.
    if (selected) return group.required ? selections : { ...selections, [group.id]: [] };
    return { ...selections, [group.id]: [itemId] };
  }
  if (selected) {
    // Checkbox: a required group must keep at least one item.
    if (group.required && existing.length === 1) return selections;
    return { ...selections, [group.id]: existing.filter(id => id !== itemId) };
  }
  return { ...selections, [group.id]: [...existing, itemId] };
};

/** True when every required group has at least one selected item. */
export const hasAllRequiredCC = (groups: CCOptionGroup[], selections: Record<number, number[]>): boolean =>
  groups.every(g => !g.required || (selections[g.id]?.length ?? 0) > 0);
